import React, { type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { HStack } from '../hstack'
import { Pressable } from '../pressable'
import { Text } from '../text'
import { type TextSize, type TextWeight } from '../text/variants'

export type NavTabProps = {
    label: ReactNode
    selected?: boolean
    onPress?: () => void
    badge?: ReactNode
    weight?: TextWeight
    size?: TextSize
    centerLabel?: boolean
    className?: string
    testID?: string
}

// Figma NavTabItem (node 7676:160716): every label runs in `content/default` —
// only the 2px `action` underline marks the selected tab. The badge
// (node 7676:160720) is a sibling of the label column, centred on it: the
// label column is 6 + 24 + 6, so its middle is the middle of the label line.
export const NavTab: React.FC<NavTabProps> = ({
    label,
    selected = false,
    onPress,
    badge,
    weight = 'medium',
    size = 'heading',
    centerLabel = false,
    className,
    testID,
}) => {
    const labelText = (
        <Text size={size} weight={weight} tone='default' className='whitespace-nowrap'>
            {label}
        </Text>
    )
    const content = centerLabel ? (
        <>
            <HStack className='flex-row items-center gap-2'>
                {labelText}
                {badge}
            </HStack>
            {selected && (
                <Box className='absolute inset-x-0 bottom-0 h-0.5 rounded bg-action-default' />
            )}
        </>
    ) : (
        <HStack className='flex-row items-center gap-2'>
            <Box className='grow pt-1.5'>
                {labelText}
                <Box className='h-1.5 w-full justify-end'>
                    {selected && <Box className='h-0.5 w-full rounded bg-action-default' />}
                </Box>
            </Box>
            {badge}
        </HStack>
    )
    const containerClassName = twMerge(
        centerLabel ? 'relative items-center justify-center py-1.5' : 'items-stretch',
        className,
    )

    return onPress ? (
        <Pressable
            onPress={onPress}
            className={containerClassName}
            testID={testID}
            accessibilityRole='tab'
            accessibilityState={{ selected }}
        >
            {content}
        </Pressable>
    ) : (
        <Box
            className={containerClassName}
            testID={testID}
            accessibilityRole='tab'
            accessibilityState={{ selected }}
        >
            {content}
        </Box>
    )
}

NavTab.displayName = 'NavTab'
